/**
 * FILE: src/layouts/notifications/NotificationTemplates/index.jsx
 * 
 * Notification Templates Management - List View
 * 
 * Features:
 * - List all notification templates with filters
 * - Category/channel/status filters
 * - Quick actions (toggle, edit, duplicate, delete)
 * - Metrics overview cards
 * - Create new template button
 * - Navigate to detailed editor
 * 
 * Uses ListPageTemplate for consistent table behavior
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmailIcon from "@mui/icons-material/Email";
import InboxIcon from "@mui/icons-material/Inbox";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import KDSnackbar from "components/KDSnackbar";
import DashboardNavbar from "components/DashboardNavbar";
import { useMaterialUIController } from "context";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import { useT } from "context/I18nContext";
import useNotificationTemplatesApi from "hooks/useNotificationTemplatesApi";

// ============================================================================
// STYLES
// ============================================================================

const cardStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  borderRadius: "16px",
  overflow: "hidden",
};

const statCardStyle = {
  ...cardStyle,
  p: 2.5,
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const labelStyle = {
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

// Category colors
const categoryColors = {
  transactional: { bg: "rgba(59, 130, 246, 0.2)", color: "#60A5FA" },
  marketing: { bg: "rgba(236, 72, 153, 0.2)", color: "#F472B6" },
  admin: { bg: "rgba(139, 92, 246, 0.2)", color: "#A78BFA" },
  system: { bg: "rgba(249, 115, 22, 0.2)", color: "#FB923C" },
};

// Priority colors
const priorityColors = {
  low: "#94A3B8",
  normal: "#60A5FA",
  high: "#F59E0B",
  urgent: "#EF4444",
};

// Channel icons
const channelIcons = {
  email: EmailIcon,
  push: NotificationsIcon,
  inapp: InboxIcon,
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

function StatCard({ icon: IconComponent, label, value, color }) {
  return (
    <Card sx={statCardStyle}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
          borderRadius: "12px",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconComponent sx={{ color, fontSize: 28 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <KDTypography sx={labelStyle}>{label}</KDTypography>
        <KDTypography variant="h4" color="white" fontWeight="bold">
          {value}
        </KDTypography>
      </Box>
    </Card>
  );
}

// ============================================================================
// DUPLICATE DIALOG
// ============================================================================

function DuplicateDialog({ open, onClose, onConfirm, templateName, t }) {
  const [newKey, setNewKey] = useState("");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open) {
      setNewKey("");
      setNewName(`${templateName} (Copy)`);
    }
  }, [open, templateName]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth 
      PaperProps={{
        sx: { 
          bgcolor: "rgba(26, 26, 46, 0.98)", 
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: "16px",
        }
      }}
    >
      <DialogTitle sx={{ color: "white" }}>{t("templates.duplicateTitle")}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label={t("templates.newKey")}
          placeholder={t("templates.newKeyPlaceholder")}
          value={newKey}
          onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
          sx={{ 
            mt: 2, 
            mb: 2, 
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
              "&:hover fieldset": { borderColor: "rgba(13, 148, 136, 0.5)" },
              "&.Mui-focused fieldset": { borderColor: "#0D9488" },
            },
            "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.6)" },
          }}
          helperText={t("templates.newKeyHelper")}
        />
        <TextField
          fullWidth
          label={t("templates.newName")}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          sx={{ 
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
              "&:hover fieldset": { borderColor: "rgba(13, 148, 136, 0.5)" },
              "&.Mui-focused fieldset": { borderColor: "#0D9488" },
            },
            "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.6)" },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: "rgba(255, 255, 255, 0.7)" }}>{t("common.cancel")}</Button>
        <Button 
          variant="contained" 
          onClick={() => onConfirm(newKey, newName)}
          disabled={!newKey}
          sx={{ bgcolor: "#0D9488", "&:hover": { bgcolor: "#0B7A6E" } }}
        >
          {t("templates.duplicate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// DELETE DIALOG
// ============================================================================

function DeleteDialog({ open, onClose, onConfirm, templateName, t }) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth 
      PaperProps={{
        sx: { 
          bgcolor: "rgba(26, 26, 46, 0.98)", 
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: "16px",
        }
      }}
    >
      <DialogTitle sx={{ color: "white" }}>{t("templates.deleteTitle")}</DialogTitle>
      <DialogContent>
        <KDTypography color="text">
          {t("templates.deleteConfirm", { name: templateName })}
        </KDTypography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: "rgba(255, 255, 255, 0.7)" }}>{t("common.cancel")}</Button>
        <Button 
          variant="contained" 
          onClick={onConfirm}
          sx={{ bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" } }}
        >
          {t("common.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// TEMPLATE ROW COMPONENT
// ============================================================================

function TemplateRow({ template, onEdit, onView, onToggle, onDuplicate, onDelete, canUpdate, canDelete, t }) {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const category = categoryColors[template.category] || categoryColors.transactional;
  const priorityColor = priorityColors[template.priority] || priorityColors.normal;
  
  return (
    <KDBox
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        borderRadius: "12px",
        mb: 1,
        backgroundColor: "rgba(255, 255, 255, 0.02)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderColor: "rgba(13, 148, 136, 0.3)",
        },
      }}
    >
      {/* Name & Key */}
      <KDBox flex={2}>
        <KDTypography variant="subtitle2" color="white" fontWeight="bold">
          {template.name}
        </KDTypography>
        <KDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontFamily: "monospace" }}>
          {template.key}
        </KDTypography>
      </KDBox>
      
      {/* Category */}
      <KDBox flex={1}>
        <Chip
          label={template.category || "—"}
          size="small"
          sx={{
            bgcolor: category.bg,
            color: category.color,
            border: `1px solid ${category.color}40`,
            fontSize: "0.7rem",
          }}
        />
      </KDBox>
      
      {/* Channels */}
      <KDBox flex={1} display="flex" gap={0.5}>
        {(template.channels || []).map((channel) => {
          const ChannelIcon = channelIcons[channel] || NotificationsIcon;
          return (
            <Tooltip key={channel} title={channel}>
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: "6px",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ChannelIcon sx={{ fontSize: 16, color: "rgba(255, 255, 255, 0.7)" }} />
              </Box>
            </Tooltip>
          );
        })}
      </KDBox>
      
      {/* Priority */}
      <KDBox flex={0.5} textAlign="center">
        <Chip
          label={template.priority || "normal"}
          size="small"
          sx={{
            bgcolor: `${priorityColor}20`,
            color: priorityColor,
            border: `1px solid ${priorityColor}40`,
            fontSize: "0.65rem",
          }}
        />
      </KDBox>
      
      {/* Status Toggle */}
      <KDBox flex={0.5} textAlign="center">
        <Switch
          checked={template.isActive || false}
          onChange={() => onToggle(template)}
          disabled={!canUpdate}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#10B981" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#10B981",
            },
          }}
        />
      </KDBox>
      
      {/* Actions */}
      <KDBox flex={0.5} display="flex" justifyContent="flex-end">
        <IconButton 
          size="small" 
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: "rgba(255, 255, 255, 0.6)" }}
        >
          <MoreVertIcon />
        </IconButton>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              bgcolor: "rgba(26, 26, 46, 0.98)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
            },
          }}
        >
          <MenuItem onClick={() => { onView(template); setAnchorEl(null); }}>
            <ListItemIcon><VisibilityIcon sx={{ color: "#60A5FA" }} fontSize="small" /></ListItemIcon>
            <ListItemText sx={{ "& .MuiTypography-root": { color: "white" } }}>{t("templates.view")}</ListItemText>
          </MenuItem>
          {canUpdate && (
            <MenuItem onClick={() => { onEdit(template); setAnchorEl(null); }}>
              <ListItemIcon><EditIcon sx={{ color: "#10B981" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: "white" } }}>{t("common.edit")}</ListItemText>
            </MenuItem>
          )}
          {canUpdate && (
            <MenuItem onClick={() => { onDuplicate(template); setAnchorEl(null); }}>
              <ListItemIcon><ContentCopyIcon sx={{ color: "#A78BFA" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: "white" } }}>{t("templates.duplicate")}</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { onDelete(template); setAnchorEl(null); }}>
              <ListItemIcon><DeleteIcon sx={{ color: "#EF4444" }} fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ "& .MuiTypography-root": { color: "white" } }}>{t("common.delete")}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </KDBox>
    </KDBox>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function NotificationTemplates() {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const ability = useAbility();
  const t = useT();
  
  const {
    listTemplates,
    getStatsOverview,
    getMetadata,
    duplicateTemplate,
    toggleTemplate: apiToggleTemplate,
    deleteTemplate: apiDeleteTemplate,
    loading: apiLoading,
  } = useNotificationTemplatesApi();

  // Store stable refs
  const listTemplatesRef = useRef(listTemplates);
  const getStatsOverviewRef = useRef(getStatsOverview);
  const duplicateTemplateRef = useRef(duplicateTemplate);
  const toggleTemplateRef = useRef(apiToggleTemplate);
  const deleteTemplateRef = useRef(apiDeleteTemplate);

  useEffect(() => {
    listTemplatesRef.current = listTemplates;
    getStatsOverviewRef.current = getStatsOverview;
    duplicateTemplateRef.current = duplicateTemplate;
    toggleTemplateRef.current = apiToggleTemplate;
    deleteTemplateRef.current = apiDeleteTemplate;
  }, [listTemplates, getStatsOverview, duplicateTemplate, apiToggleTemplate, apiDeleteTemplate]);

  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);

  // Dialog state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, color: "info", title: "", content: "" });

  // Permissions
  const canCreate = ability.can(ACTIONS.CREATE, SUBJECTS.NOTIFICATION_TEMPLATE);
  const canUpdate = ability.can(ACTIONS.UPDATE, SUBJECTS.NOTIFICATION_TEMPLATE);
  const canDelete = ability.can(ACTIONS.DELETE, SUBJECTS.NOTIFICATION_TEMPLATE);

  const showSnackbar = (color, title, content = "") => {
    setSnackbar({ open: true, color, title, content });
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesData, statsData] = await Promise.all([
        listTemplatesRef.current({
          category: categoryFilter !== "all" ? categoryFilter : undefined,
          isActive: showInactiveOnly ? false : undefined,
        }),
        getStatsOverviewRef.current().catch(() => null),
      ]);

      setTemplates(templatesData?.templates || []);
      setStats(statsData);
    } catch (err) {
      console.error("Error loading templates:", err);
      showSnackbar("error", t("common.error"), err.message);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, showInactiveOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleEdit = (template) => {
    navigate(`/admin/notification-templates/${template.id}/edit`);
  };

  const handleView = (template) => {
    // View uses the same editor in read mode (can be extended later for view-only)
    navigate(`/admin/notification-templates/${template.id}/edit`);
  };

  const handleToggle = async (template) => {
    try {
      await toggleTemplateRef.current(template.id);
      showSnackbar("success", t("templates.updated"), template.isActive ? t("templates.deactivated") : t("templates.activated"));
      loadData();
    } catch (err) {
      showSnackbar("error", t("common.error"), err.message);
    }
  };

  const handleDuplicate = (template) => {
    setSelectedTemplate(template);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = async (newKey, newName) => {
    try {
      await duplicateTemplateRef.current(selectedTemplate.id, newKey, newName);
      showSnackbar("success", t("templates.duplicated"));
      loadData();
    } catch (err) {
      showSnackbar("error", t("common.error"), err.message);
    }
    setDuplicateDialogOpen(false);
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTemplateRef.current(selectedTemplate.id);
      showSnackbar("success", t("templates.deleted"));
      loadData();
    } catch (err) {
      showSnackbar("error", t("common.error"), err.message);
    }
    setDeleteDialogOpen(false);
  };

  const handleCreateNew = () => {
    navigate("/admin/notification-templates/new");
  };

  // Filter templates
  const filteredTemplates = templates.filter((t) => {
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    return true;
  });

  return (
    <KDBox
      sx={({ breakpoints, transitions }) => ({
        p: 3,
        marginLeft: miniSidenav ? "80px" : "260px",
        transition: transitions.create("margin-left", {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        [breakpoints.down("xl")]: { marginLeft: 0 },
      })}
    >
      <DashboardNavbar />
      
      <KDBox mt={4}>
        {/* Header */}
        <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <KDBox>
            <KDTypography variant="h4" color="white" fontWeight="bold">
              {t("templates.title")}
            </KDTypography>
            <KDTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
              {t("templates.description")}
            </KDTypography>
          </KDBox>
          
          {canCreate && (
            <KDButton
              variant="gradient"
              color="info"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              {t("templates.create")}
            </KDButton>
          )}
        </KDBox>
        
        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={NotificationsIcon}
              label={t("templates.totalTemplates")}
              value={stats?.total || templates.length}
              color="#0D9488"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={CheckCircleIcon}
              label={t("templates.active")}
              value={stats?.active || templates.filter(tpl => tpl.isActive).length}
              color="#10B981"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SendIcon}
              label={t("templates.sent24h")}
              value={stats?.last24h?.sent || "—"}
              color="#3B82F6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={CancelIcon}
              label={t("templates.inactive")}
              value={stats?.inactive || templates.filter(tpl => !tpl.isActive).length}
              color="#EF4444"
            />
          </Grid>
        </Grid>
        
        {/* Category Filter Tabs */}
        <Card sx={{ ...cardStyle, mb: 3, p: 2 }}>
          <KDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Tabs
              value={categoryFilter}
              onChange={(e, v) => setCategoryFilter(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  color: "rgba(255, 255, 255, 0.6)",
                  minHeight: 36,
                  py: 0,
                },
                "& .Mui-selected": { color: "#5EEAD4" },
                "& .MuiTabs-indicator": { bgcolor: "#0D9488" },
              }}
            >
              <Tab label={t("templates.category_all")} value="all" />
              <Tab label={t("templates.category_transactional")} value="transactional" />
              <Tab label={t("templates.category_marketing")} value="marketing" />
              <Tab label={t("templates.category_admin")} value="admin" />
              <Tab label={t("templates.category_system")} value="system" />
            </Tabs>

            <Box sx={{ flex: 1 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={showInactiveOnly}
                  onChange={(e) => setShowInactiveOnly(e.target.checked)}
                  size="small"
                />
              }
              label={<KDTypography variant="caption" color="white">{t("templates.inactiveOnly")}</KDTypography>}
            />
          </KDBox>
        </Card>
        
        {/* Templates List */}
        <Card sx={{ ...cardStyle, p: 2 }}>
          {/* Table Header */}
          <KDBox
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              mb: 1,
            }}
          >
            <KDTypography flex={2} variant="caption" fontWeight="bold" color="text" sx={{ opacity: 0.6 }}>
              {t("templates.col_template")}
            </KDTypography>
            <KDTypography flex={1} variant="caption" fontWeight="bold" color="text" sx={{ opacity: 0.6 }}>
              {t("templates.col_category")}
            </KDTypography>
            <KDTypography flex={1} variant="caption" fontWeight="bold" color="text" sx={{ opacity: 0.6 }}>
              {t("templates.col_channels")}
            </KDTypography>
            <KDTypography flex={0.5} variant="caption" fontWeight="bold" color="text" textAlign="center" sx={{ opacity: 0.6 }}>
              {t("templates.col_priority")}
            </KDTypography>
            <KDTypography flex={0.5} variant="caption" fontWeight="bold" color="text" textAlign="center" sx={{ opacity: 0.6 }}>
              {t("templates.col_status")}
            </KDTypography>
            <KDTypography flex={0.5} variant="caption" fontWeight="bold" color="text" textAlign="right" sx={{ opacity: 0.6 }}>
              {t("templates.col_actions")}
            </KDTypography>
          </KDBox>
          
          {loading ? (
            <KDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: "#0D9488" }} />
            </KDBox>
          ) : filteredTemplates.length === 0 ? (
            <KDBox textAlign="center" py={6}>
              <NotificationsIcon sx={{ fontSize: 48, color: "rgba(255, 255, 255, 0.2)", mb: 2 }} />
              <KDTypography color="white">{t("templates.empty")}</KDTypography>
              {canCreate && (
                <KDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{ mt: 2 }}
                >
                  {t("templates.createFirst")}
                </KDButton>
              )}
            </KDBox>
          ) : (
            filteredTemplates.map((template) => (
              <TemplateRow
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onView={handleView}
                onToggle={handleToggle}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                canUpdate={canUpdate}
                canDelete={canDelete}
                t={t}
              />
            ))
          )}
        </Card>
      </KDBox>
      
      {/* Dialogs */}
      <DuplicateDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={handleDuplicateConfirm}
        templateName={selectedTemplate?.name || ""}
        t={t}
      />
      
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        templateName={selectedTemplate?.name || ""}
        t={t}
      />
      
      {/* Snackbar */}
      <KDSnackbar
        color={snackbar.color}
        icon="check"
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        close={() => setSnackbar({ ...snackbar, open: false })}
        bgWhite
      />
    </KDBox>
  );
}
